import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Copy, Download, Share2 } from 'lucide-react';
import { toast } from 'sonner';

interface PackingItem {
  id: string;
  name: string;
  category: string;
  essential: boolean;
  quantity?: number;
  packed: boolean;
}

interface SharePackingListModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: PackingItem[];
  destination: string;
  tripType: string;
  tripLength: number;
}

export function SharePackingListModal({
  open,
  onOpenChange,
  items,
  destination,
  tripType,
  tripLength
}: SharePackingListModalProps) {
  const [format, setFormat] = useState<'text' | 'checklist'>('text');

  const generateTextContent = () => {
    const header = `🧳 Packing List for ${destination}\nTrip Type: ${tripType} | Duration: ${tripLength} days\n\n`;
    
    const grouped = items.reduce((acc, item) => {
      if (!acc[item.category]) {
        acc[item.category] = [];
      }
      acc[item.category].push(item);
      return acc;
    }, {} as Record<string, PackingItem[]>);

    let content = header;
    
    Object.entries(grouped).forEach(([category, categoryItems]) => {
      content += `\n${category.toUpperCase()}\n`;
      content += '─'.repeat(30) + '\n';
      
      categoryItems.forEach(item => {
        const checkbox = format === 'checklist' ? (item.packed ? '✓' : '☐') : '•';
        const qty = item.quantity ? ` (x${item.quantity})` : '';
        const essential = item.essential ? ' *' : '';
        content += `${checkbox} ${item.name}${qty}${essential}\n`;
      });
    });

    content += '\n\n* Essential items\n';
    content += `\nGenerated by OdyVoy - Your AI Travel Companion`;
    
    return content;
  };

  const copyToClipboard = async () => {
    try {
      const content = generateTextContent();
      await navigator.clipboard.writeText(content);
      toast.success('Copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const downloadAsText = () => {
    const content = generateTextContent();
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `packing-list-${destination.toLowerCase().replace(/\s+/g, '-')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Downloaded successfully');
  };

  const shareViaWhatsApp = () => {
    const content = generateTextContent();
    const encoded = encodeURIComponent(content);
    window.open(`https://wa.me/?text=${encoded}`, '_blank');
  };

  const shareViaEmail = () => {
    const content = generateTextContent();
    const subject = encodeURIComponent(`Packing List for ${destination}`);
    const body = encodeURIComponent(content);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Share Packing List</DialogTitle>
          <DialogDescription>
            Share your packing list via WhatsApp, email, or download as text
          </DialogDescription>
        </DialogHeader>

        <Tabs value={format} onValueChange={(v) => setFormat(v as 'text' | 'checklist')}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="text">Plain Text</TabsTrigger>
            <TabsTrigger value="checklist">Checklist</TabsTrigger>
          </TabsList>

          <TabsContent value={format} className="mt-4">
            <div className="bg-muted p-4 rounded-lg max-h-[300px] overflow-y-auto font-mono text-sm whitespace-pre-wrap">
              {generateTextContent()}
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <div className="flex gap-2 flex-wrap">
            <Button onClick={copyToClipboard} variant="outline" size="sm">
              <Copy className="h-4 w-4 mr-2" />
              Copy
            </Button>
            <Button onClick={downloadAsText} variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={shareViaWhatsApp} size="sm">
              <Share2 className="h-4 w-4 mr-2" />
              WhatsApp
            </Button>
            <Button onClick={shareViaEmail} variant="secondary" size="sm">
              <Share2 className="h-4 w-4 mr-2" />
              Email
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
